import clsx from "clsx";
import type { ChangeEvent, TextareaHTMLAttributes } from "react";
import { forwardRef, useState } from "react";

import Button from "../Button";
import { Field } from "../Input";
import { Label } from "../Label";

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  controlled?: boolean;
  invalid?: boolean;
  isFullWidth?: boolean;
  label?: string;
  onChange?: (value: string) => void;
  onConfirm?: (value: string) => void;
  toolTipText?: string;
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      controlled = false,
      label,
      toolTipText,
      invalid,
      isFullWidth,
      onChange,
      required,
      disabled,
      onConfirm,
      ...props
    },
    ref,
  ) => {
    const [text, setText] = useState(props.value ?? props.defaultValue ?? "");
    const [isEditing, setIsEditing] = useState(false);

    const currentValue = controlled ? (props.value ?? "") : text;

    const handleFocus = () => {
      setIsEditing(true);
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
      if (!controlled) {
        setText(e.target.value);
      }
    };

    const handleSave = () => {
      onConfirm?.(currentValue.toString());
      setIsEditing(false);
    };

    const handleCancel = () => {
      if (!controlled) {
        setText(props.value ?? props.defaultValue ?? "");
      }
      setIsEditing(false);
    };

    return (
      <Field disabled={disabled} className={isFullWidth ? "w-full" : ""}>
        {label && (
          <Label
            required={required}
            toolTipText={toolTipText}
            disabled={disabled}
          >
            {label}
          </Label>
        )}
        <div className="relative">
          <textarea
            ref={ref}
            disabled={disabled}
            {...props}
            value={currentValue}
            onFocus={handleFocus}
            onChange={handleChange}
            className={clsx(
              "block w-full rounded-md border px-2 lg:px-3 py-2 text-gray-900 shadow-sm text-xs lg:text-sm",
              invalid
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "focus:outline-none focus:ring-2 focus:ring-opacity-50",
              "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
              props.maxLength && "pr-12",
              className,
            )}
          />
          {props.maxLength && (
            <span className="absolute bottom-2 right-2 text-right text-[10px] lg:text-xs text-gray-400">
              {currentValue?.toString().length}/{props.maxLength}
            </span>
          )}
        </div>
        {isEditing && onConfirm && (
          <div className="flex gap-2 justify-end">
            <Button onClick={handleSave} size="sm">
              Save
            </Button>
            <Button onClick={handleCancel} size="sm" plain>
              Cancel
            </Button>
          </div>
        )}
      </Field>
    );
  },
);

TextArea.displayName = "TextArea";

export default TextArea;
